import { createContext, useContext } from "react";
import { type User } from "firebase/auth";
import { type ConnectorDeniedState, type UserProfile } from "./useAuth";

export interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loadingUserData: boolean;
  backendReachable: boolean;
  connectorDenied: ConnectorDeniedState | null;
  signUp: ({
    email,
    password,
    displayName,
  }: {
    email: string;
    password: string;
    displayName: string;
  }) => Promise<{ user: User | null; error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: Error | null }>;
  signInWithGoogle: () => Promise<{ user: User | null; error: Error | null }>;
  sendVerificationEmail: () => Promise<{ error: Error | null }>;
  refreshUser: () => Promise<void>;
  logout: () => Promise<{ error: Error | null }>;
  updateUserProfile: (displayName: string, photoURL: string) => Promise<void>;
  hasRole: (role: string) => boolean;
  isUserSuperAdmin: () => boolean;
  isAccountAdmin: () => boolean;
  getAccountId: () => string | undefined;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
